import * as React from 'react';
import { Dispatch } from 'react-redux';
import { Api } from '../typings/api';
import store from '../store';
import { StoreState } from '../reducers/index';
import NativeApi from './nativeApi';
import UrlUtils from './url';
import UserAgentUtils from './userAgent';
import { AppType, AppInfosState } from '../containers/App/reducer';
import { GalleryInfosState } from '../containers/Gallery/reducer';
import { DriveListState, DriveItemInfo } from '../containers/DriveItemList/reducer';
import { AuthenticationUserState } from '../containers/Authentication/reducer';
import { DownloadStatus, OfflineManagerState, OfflineManagerStatus } from '../containers/OfflineManager/reducer';
import { RoutePath, RouteParams, RouteParamsDriveItemListPage, RouteParamsDriveItemDetailPage, RouteParamsTheater, RouteParamsStaticPage } from '../routes/constants';
import { DriveItemListOrigin } from '../containers/DriveItemListPage/constants';
import { getOrFetchDriveItemMeta } from '../containers/DriveItemList/actions';
import DriveItemUtils from './driveItem';
import ApiUtils from './api';
import AuthUtils from './authentication';
import { MultiAppAuthenticationCredentials } from '../containers/MultiAppAuthentication/reducer';
import { updateCredentials, signOut } from '../containers/MultiAppAuthentication/actions';
import { ThumbType, LogoType } from '../containers/OfflineManager/reducer';
import { MenuItemType } from '../typings/menu';
import { RouterParamsState } from '../containers/SetRouteParams/reducer';
import LocalStorageManager from './localStorage/manager';
import { throttle } from './optimization';

const defaultBrandIcon = require('../icons/webpublication.svg');
const defaultMultiAppBarIcon = require('../icons/uprez_bar.svg');
const defaultMultiAppIcon = require('../icons/uprez.svg');

const I18n = require('react-redux-i18n').I18n;

export interface HttpHeader {
	key: string;
	value: string;
}

export interface GalleryCredentials {
	login: string;
	password: string;
}

export enum ScreenFormat {
	Tablet = 'tablet',
	Mobile = 'mobile',
	Desktop = 'desktop'
}

export enum ServerStatusCode {
	OK = 200,
	NO_CONTENT = 204,
	UNAUTHORIZED = 401,
	FORBIDDEN = 403,
	CONFLICT = 409,
}

enum HttpHeaderType {
	CONTENT_TYPE = 'Content-Type',
	LOCATION = 'Location',
}

export const downloadsFolder = 'downloads';
export const tempFolder = 'tmp';
export const thumbnailsFolder = 'thumbnails';
export const iconsFolder = 'icons';

export default class Utils {
	static UPLOAD_PROGRESS_DELAY: number = 1000;
	static deviceRootPath = window.location.origin + window.location.pathname.replace('indexConteneur.html', '') + '../..';
	static cancelableRequests: { [k: string]: XMLHttpRequest } = {};

	static isGalleryReady = (galleryInfos: GalleryInfosState, offlineManager: OfflineManagerState, appInfos: AppInfosState): boolean => {
		return galleryInfos.isConfigLoaded && Utils.isTemplateLoaded(appInfos, galleryInfos) && offlineManager.status === OfflineManagerStatus.INITIALIZED;
	}

	static getGalleryName = (config: Api.Config, driveList: DriveListState, dispatch: Dispatch<StoreState>): string => {
		if (!driveList.driveItems[config.applicationGallery]) {
			dispatch(getOrFetchDriveItemMeta(config.applicationGallery));
			return '';
		}
		return I18n.t('page_title.gallery_name', { galleryName: DriveItemUtils.getDisplayName(driveList.driveItems[config.applicationGallery])});
	}

	static isTemplateLoaded = (appInfos: AppInfosState, galleryInfos: GalleryInfosState): boolean => {
		const { isLocalTemplateLoaded, isRemoteTemplateLoaded } = galleryInfos;
		const isMobileMultiApp = Utils.isMobileMultiApp(appInfos);
		return isMobileMultiApp ? isRemoteTemplateLoaded : isLocalTemplateLoaded;
	}

	static getItemName = (driveList: DriveListState, id: number, dispatch: Dispatch<StoreState>): string => {
		if (!driveList.driveItems[id]) {
			dispatch(getOrFetchDriveItemMeta(id));
			return '';
		}
		return DriveItemUtils.getDisplayName(driveList.driveItems[id]);
	}

	static getPageTitle = (route: RoutePath, params: RouteParams, config: Api.Config, driveList: DriveListState, dispatch: Dispatch<StoreState>): string => {
		const galleryName = Utils.getGalleryName(config, driveList, dispatch);
		let castedParams;
		let itemName;

		if (!galleryName) {
			return '';
		}

		// All RoutePath must be in the switch, do not add a default case
		switch (route) {
			case RoutePath.Default:
			case RoutePath.Home:
				return galleryName;
			case RoutePath.Authentication:
				return I18n.t('page_title.authentication');
			case RoutePath.ParametersPage:
				return I18n.t('page_title.parameters', { galleryName });
			case RoutePath.ProfilePage:
				return I18n.t('page_title.profile');
			case RoutePath.ProposalStatsPage:
				return I18n.t('page_title.my_sent');
			case RoutePath.DriveItemListPage:
				castedParams = params as RouteParamsDriveItemListPage;
				return Utils.getDriveItemListPageTitle(castedParams, galleryName, driveList, dispatch);
			case RoutePath.Theater:
			case RoutePath.TheaterList:
				castedParams = params as RouteParamsTheater;
				itemName = driveList && castedParams.driveItemId ? Utils.getItemName(driveList, castedParams.driveItemId, dispatch) : '';
				return itemName ? I18n.t('page_title.theater', { galleryName, itemName }) : '';
			case RoutePath.DriveItemDetailPage:
				castedParams = params as RouteParamsDriveItemDetailPage;
				itemName = driveList && castedParams.driveItemId ? Utils.getItemName(driveList, castedParams.driveItemId, dispatch) : '';
				return itemName ? I18n.t('page_title.details', { galleryName, itemName }) : '';
			case RoutePath.NotFound:
				return I18n.t('messages.page_not_found');
			case RoutePath.StaticPage:
				castedParams = params as RouteParamsStaticPage;
				itemName = driveList && castedParams.component ? Utils.getItemName(driveList, parseInt(castedParams.component, 10), dispatch) : '';
				return `${itemName} - ${galleryName}`;
			case RoutePath.Editor:
				return I18n.t('page_title.editor');
		}
	}

	static getDriveItemListPageTitle = (params: RouteParamsDriveItemListPage, galleryName: string, driveList, dispatch): string => {
		const origin: DriveItemListOrigin = params.origin;

		switch (origin) {
			case DriveItemListOrigin.Downloads:
				return I18n.t('page_title.downloads', { galleryName });
			case DriveItemListOrigin.Selection:
				return I18n.t('page_title.selection', {galleryName});
			case DriveItemListOrigin.Folder: {
				if (!params.originParam) {
					return I18n.t('page_title.drive', { galleryName });
				}
				const itemName = driveList && params.originParam ? Utils.getItemName(driveList, parseInt(params.originParam, 10), dispatch) : '';
				return itemName ? I18n.t('page_title.folder', { galleryName, itemName }) : '';
			}
			case DriveItemListOrigin.New:
				return I18n.t('page_title.new', { galleryName });
			case DriveItemListOrigin.Search:
				return I18n.t('page_title.search', { galleryName });
			case DriveItemListOrigin.Assortment:
				return I18n.t('page_title.assortment', { galleryName });
		}
	}

	static getBrandLogoName = (template: Api.Template, logoType: LogoType): string => {
		return template.brand && template.brand[logoType] ? template.brand[logoType] : '';
	}

	static getDefaultLogo = (isHome: boolean, isMultiApp: boolean): string => {
		if (isMultiApp) {
			return isHome ? defaultMultiAppIcon : defaultMultiAppBarIcon;
		}

		return defaultBrandIcon;
	}

	static getOriginBrandLogoUrl = (galleryInfos: GalleryInfosState, isHome: boolean, isMultiApp: boolean, template?: Api.Template): string => {
		const resolvedTemplate = template || galleryInfos.template;
		const logoType = isHome ? LogoType.HOME_LOGO : LogoType.BRAND_LOGO;

		if (resolvedTemplate) {
			if (resolvedTemplate.brand && resolvedTemplate.brand[logoType]) {
				const brandLogo = resolvedTemplate.brand[logoType];
				const brandLogoFilename = Utils.getFilenameFromMediaId(brandLogo);
				return UrlUtils.getItemOriginalFileUrl(galleryInfos.config, brandLogoFilename);
			}
			return Utils.getDefaultLogo(isHome, isMultiApp);
		}
		return '';
	}

	static isMaster = (galleryConfig: Api.Config): boolean => {
		return galleryConfig.mediaPath !== undefined;
	}

	static getComponentUrl = (galleryInfos: GalleryInfosState, componentId: number, isAvailableOffline: boolean, isWelcomePage: boolean): string => {
		if (UserAgentUtils.isMobileApp && isAvailableOffline) {
			return `${Utils.getPersistentDownloadsFolder()}/${componentId}/index.html`;
		}

		return isWelcomePage ? UrlUtils.getWelcomePageUrl(galleryInfos) : UrlUtils.getComponentUrl(galleryInfos, componentId);
	}

	static getGalleryCredentials = (userInfos?: AuthenticationUserState): GalleryCredentials | null => {
		if (userInfos === undefined) {
			const storeState = store.getState();
			userInfos = storeState.authentication.userInfos;
		}
		const { mode } = userInfos;
		const isConnectedExcel = AuthUtils.isConnected(mode) && AuthUtils.isExcel(mode);

		if (userInfos.needCredentials && isConnectedExcel) {
			return {
				login: userInfos.login,
				password: userInfos.password,
			};
		} else {
			return null;
		}
	}

	static getMultiAppCredentials = (): MultiAppAuthenticationCredentials | undefined => {
		const storeState = store.getState();
		return storeState.multiAppAuthenticationInfos.credentials;
	}

	static getWelcomePageDownloadUrl = (galleryInfos: GalleryInfosState, template?: Api.Template): string => {
		const resolvedTemplate = template || galleryInfos.template;

		if (resolvedTemplate && resolvedTemplate.welcomepage) {
			const welcomePageUrl = resolvedTemplate.welcomepage.url;
			const welcomePageId = Utils.getMediaId(welcomePageUrl);
			return Utils.getComponentDownloadUrlById(galleryInfos, welcomePageId);
		}
		return '';
	}

	static getComponentDownloadUrl = (galleryInfos: GalleryInfosState, component: Api.DriveItem): string => {
		const componentId = component.id;
		return `${Utils.getComponentDownloadUrlById(galleryInfos, componentId)}`;
	}

	static getComponentDownloadUrlById = (galleryInfos: GalleryInfosState, componentId: number): string => {
		return `${UrlUtils.getResourceZipUrl(galleryInfos.config, componentId)}`;
	}

	static getMediaId(item: string): number {
		const regexMatches = /^\$\{media\}(.+)$/.exec(item);
		return regexMatches !== null ? parseInt(regexMatches[1], 10) : -1;
	}

	static getFilenameFromMediaId(mediaId: string): string {
		const filenameResult = /^\$media\$(.+)$/.exec(mediaId);
		return filenameResult ? filenameResult[1] : '';
	}

	static getBrandLogoUrl = (galleryInfos: GalleryInfosState, offlineManager: OfflineManagerState, isHome: boolean, isMultiApp: boolean): string => {
		if (UserAgentUtils.isMobileApp) {
			const thumbType = isHome ? ThumbType.HOME_THUMB : ThumbType.BRAND_THUMB;
			const logoType = isHome ? LogoType.HOME_LOGO : LogoType.BRAND_LOGO;

			const { remoteConfig, localConfig } = offlineManager[thumbType];

			if (galleryInfos.isRemoteTemplateLoaded && remoteConfig.filename && remoteConfig.status === DownloadStatus.DOWNLOADED) {
				return `${Utils.getThumbnailsFolder()}/brand/${Utils.getFilenameFromMediaId(remoteConfig.filename)}`;
			} else if (!remoteConfig.filename && localConfig.filename && localConfig.status === DownloadStatus.DOWNLOADED) {
				return `${Utils.getThumbnailsFolder()}/brand/${Utils.getFilenameFromMediaId(localConfig.filename)}`;
			}

			if (galleryInfos.isRemoteTemplateLoaded && !Utils.getBrandLogoName(galleryInfos.template, logoType)) {
				return Utils.getDefaultLogo(isHome, isMultiApp); // if no logo has been set, we use the webpublication one
			}
			return ''; // if logo is downloading, nothing is rendered to avoid having several logos shown
		}
		return Utils.getOriginBrandLogoUrl(galleryInfos, isHome, isMultiApp);
	}

	static getDeviceThumbnailPath = (galleryConfig: Api.Config, driveItem: Api.DriveItem, resolutions: { [k: string]: Api.ResolutionGroup }, isIcon: boolean) => {
		const thumbPath = isIcon ? DriveItemUtils.getIconOriginalPath(driveItem, galleryConfig) : DriveItemUtils.getThumbnail(driveItem, galleryConfig, resolutions, true);
		const extension = Utils.getFileExtension(thumbPath);
		return isIcon ? `${Utils.getIconsFolder()}/${driveItem.id}.${extension}` : `${Utils.getThumbnailsFolder()}/${driveItem.id}/card_thumbnail.${extension}`;
	}

	static getCustomIconUrl = (galleryConfig: Api.Config, driveItem: Api.DriveItem, resolutions: { [k: string]: Api.ResolutionGroup }): string => {
		if (UserAgentUtils.isMobileApp) {
			return `${Utils.getDeviceThumbnailPath(galleryConfig, driveItem, resolutions, true)}`;
		}
		return UrlUtils.getItemOriginalFileUrl(galleryConfig, driveItem.originalFile);
	}

	static getPersistentDownloadsFolder = (): string => {
		return `${Utils.deviceRootPath}/${downloadsFolder}`;
	}

	static getRelativeDownloadsFolder = (): string => {
		return downloadsFolder;
	}

	static getTemporaryDownloadsFolder = (): string => {
		return `${Utils.deviceRootPath}/${tempFolder}`;
	}

	static getThumbnailsFolder = (): string => {
		return `${Utils.deviceRootPath}/${thumbnailsFolder}`;
	}

	static getIconsFolder = (): string => {
		return `${Utils.deviceRootPath}/${iconsFolder}`;
	}

	static formatFileSize(size: number): string {
		const units = ['o', 'Ko', 'Mo', 'Go', 'To'];
		let tmpSize = size;
		let i;

		for (i = 0; i < units.length; i++) {
			if (tmpSize < 1024) {
				return `${tmpSize.toFixed(1)} ${units[i]}`;
			}
			tmpSize = tmpSize / 1024;
		}
		return `${tmpSize.toFixed(1)} ${units[i]}`;
	}

	// Only works for pure Json object (functions & dates do not get copied properly)
	static deepCopy = <T>(source: T): T => {
		return JSON.parse(JSON.stringify(source));
	}

	static getSvgIcon(iconFileName: string, attributes: React.SVGProps<SVGUseElement> = {}): React.ReactNode {
		const useNode = React.createElement('use', { xlinkHref: `#svgicon-${iconFileName}`});
		return React.createElement('svg', attributes, useNode);
	}

	static nl2br(str: string): string {
		return str.replace(/(?:\r\n|\r|\n)/g, '<br />');
	}

	static promiseXHR(
		protocol: string,
		url: string,
		headers: HttpHeader[] = [],
		isAsync: boolean = true,
		dataInUrl: {} | null = null,
		dataInBody: BodyInit | undefined = undefined,
		attempt: number = 0,
		ignoreConnectionStatus: boolean = false,
		withCredentials: boolean = true,
		preventRedirect: boolean = true,
		onProgress?: (e: ProgressEvent) => void,
		requestId?: string,
	): Promise<XMLHttpRequest> {
		return new Promise<XMLHttpRequest>((resolve, reject) => {
			const isOnline = UserAgentUtils.isOnline();
			if (ignoreConnectionStatus === false && isOnline !== true) {
				if (isOnline === false) {
					console.warn('xhr rejected: connection status is false', url);
					reject();
				}
				console.warn('xhr rejected: connection status not initialised', url);
				reject();
			}

			let urlWithParams = url;
			let payload = dataInBody;

			if (dataInUrl) {
				urlWithParams += Utils.getUrlSeparator(urlWithParams) + Utils.dataToUrl(dataInUrl);
			}

			if (preventRedirect) {
				urlWithParams += Utils.getUrlSeparator(urlWithParams) + 'preventRedirect=true';
			}

			const xhr = new XMLHttpRequest();

			if (requestId) {
				Utils.cancelableRequests[requestId] = xhr;
			}

			xhr.withCredentials = withCredentials;
			xhr.open(protocol, urlWithParams, isAsync);

			headers.forEach(header => {
				if (header.key === 'overrideMimeType') {
					xhr.overrideMimeType(header.value);
				} else {
					xhr.setRequestHeader(header.key, header.value);
				}
			});

			xhr.onload = () => {
				const { status, responseURL } = xhr;
				const isForbiddenRequest = [
					ServerStatusCode.UNAUTHORIZED,
					ServerStatusCode.FORBIDDEN,
				].includes(status) && !responseURL.includes('logout');

				const { multiAppAuthenticationInfos: { isAnotherUserLogin } } = store.getState();

				// do not auto-login in case another user logged in
				if (isForbiddenRequest && !isAnotherUserLogin) {
					Utils.handleForbiddenRequest(
						xhr, resolve, reject, protocol, url, headers, isAsync, dataInUrl, dataInBody, attempt, ignoreConnectionStatus, withCredentials, preventRedirect, onProgress, requestId,
					);
					return;
				}

				resolve(xhr);

				if (requestId) {
					delete Utils.cancelableRequests[requestId];
				}
			};

			xhr.onabort = () => {
				reject(xhr);

				if (requestId) {
					delete Utils.cancelableRequests[requestId];
				}
			};

			// The hack resolving the promise if (xhr.readyState === 4 && xhr.status === 0) has been deleted, it may be useful for cross domain requests
			xhr.onerror = () => {
				const isNativeApiCall = url.includes(NativeApi.targetUrl);
				if (isNativeApiCall) {
					Utils.handleNativeApiBug(
						xhr, resolve, reject,
						protocol, url, headers, isAsync, dataInUrl, dataInBody, attempt, ignoreConnectionStatus, withCredentials, preventRedirect, onProgress, requestId
					);

					return;
				}

				reject(xhr);

				if (requestId) {
					delete Utils.cancelableRequests[requestId];
				}
			};

			if (onProgress) {
				xhr.upload.onprogress = throttle(onProgress, Utils.UPLOAD_PROGRESS_DELAY);
			}

			xhr.send(payload);
		});
	}

	static async handleForbiddenRequest(
		xhr: XMLHttpRequest,
		resolve: (value?: XMLHttpRequest | PromiseLike<XMLHttpRequest> | undefined) => void,
		reject: (reason?: {}) => void,
		protocol: string,
		url: string,
		headers: HttpHeader[],
		isAsync: boolean,
		dataInUrl: {} | null,
		dataInBody: BodyInit | undefined,
		attempt: number,
		ignoreConnectionStatus: boolean,
		withCredentials: boolean,
		preventRedirect: boolean,
		onProgress?: (e: ProgressEvent) => void,
		requestId?: string,
	): Promise<void> {
		const { status, responseURL, responseText } = xhr;

		const multiAppCredentials = Utils.getMultiAppCredentials();
		const login = multiAppCredentials
			? multiAppCredentials.login
			: LocalStorageManager.getUsername();
		const password = multiAppCredentials
			? multiAppCredentials.password
			: LocalStorageManager.getPassword();

		const location = xhr.getResponseHeader(HttpHeaderType.LOCATION);
		const isRedirectToExternalLoginPage = status === ServerStatusCode.FORBIDDEN && location && location !== '#' && (!login || !password);

		if (isRedirectToExternalLoginPage) {
			// display external login page
			// after succesfull login this page will set username, password (if user checked Remember me) and token to localStorage and redirect back to current page
			const { location: { origin, href: currentRoute } } = window;
			let domain = origin;

			if (origin.includes('localhost')) { // for dev only
				const {
					galleryInfos: {
						config: {
							service: { host = 'https://migrationv3.zone-secure.net' } = {},
						},
					},
				} = store.getState();
				domain = Utils.getUrlWithHttpsProtocol(host);
			} else if (location && location.includes('http')) {
				domain = '';
			}

			const loginUrl = `${domain}${location}&redirectTo=${encodeURIComponent(currentRoute)}`;
			window.location.href = loginUrl;
		} else {
			const { galleryInfos: { config } } = store.getState();
			const loginUrl = UrlUtils.getLoginToAspUrl(config);

			if (login && password) {
				try {
					// in case password was changed in another browser or device we get INVALID_CREDENTIALS response from appLogin call
					const isInvalidCredentials = responseURL.includes(loginUrl) && responseText.includes('INVALID_CREDENTIALS');
					// in case there are credentials for previously logged in user in localStorage (in web version) we get forbidden request after successfull appLogin call
					const isAnotherLoggedInUser = attempt > 0;

					if (isInvalidCredentials || isAnotherLoggedInUser) {
						reject(xhr);
						return store.dispatch(signOut());
					}

					const { token } = await Utils.fetchJson<Api.AppLoginResponse>(loginUrl, 'POST', true, true, {
						login,
						password,
					});

					if (multiAppCredentials) {
						store.dispatch(updateCredentials({ token }));
					} else {
						LocalStorageManager.setToken(token);
					}

					const response = await Utils.promiseXHR(
						protocol,
						url,
						headers,
						isAsync,
						{ ...dataInUrl, token },
						dataInBody,
						attempt + 1,
						ignoreConnectionStatus,
						withCredentials,
						preventRedirect,
						onProgress,
						requestId,
					);

					resolve(response);
				} catch (error) {
					reject(error);
				}
			} else {
				if (UserAgentUtils.isMobileApp) {
					return reject(xhr);
				}

				try {
					// in web version we have to call app url to redirect to external login page if we don't have credentials in localStorage
					const appUrl = UrlUtils.getApplicationUrl(config);
					await Utils.promiseXHR('GET', appUrl);
				} catch (error) {
					reject(xhr);
				}
			}
		}
	}

	static dataToUrl(data: {}, mustEncode: boolean = true): string {
		let url = '';

		for (let i in data) {
			if (data[i] instanceof Array) {
				for (let j = 0; j < data[i].length; j++) {
					const key = mustEncode ? encodeURIComponent(`${i}`) : `${i}`;
					const value = mustEncode ? encodeURIComponent(data[i][j]) : data[i][j];
					url += `&${key}=${value}`;
				}
			} else {
				if (url) {
					url += '&';
				}
				url += `${i}=${mustEncode ? encodeURIComponent(data[i]) : data[i]}`;
			}

		}

		return url;
	}

	static fetchJson<T>(
		url: string,
		protocol: string = 'GET',
		isAsync: boolean = true,
		ignoreConnectionStatus: boolean = false,
		dataInUrl: {} | null = null,
		dataInBody: BodyInit | undefined = undefined,
		isPlainTextPayload: boolean = true,
		withCredentials: boolean = true,
		preventRedirect: boolean = true,
		preventSecurityHeader: boolean = false,
		onProgress?: (e: ProgressEvent) => void,
		requestId?: string,
	): Promise<{} & T> {
		return Utils.fetchText(
			url, protocol, isAsync, ignoreConnectionStatus, dataInUrl, dataInBody, isPlainTextPayload, withCredentials, preventRedirect, preventSecurityHeader, onProgress, requestId
		)
			.then(responseText => responseText && JSON.parse(responseText));
	}

	static encodeFormPayload(fields: {}) {
		const fieldKeys = Object.keys(fields);
		const uriParameters: Array<string> = [];
		fieldKeys.forEach(fieldKey => {
			const encodedKey = encodeURIComponent(fieldKey);
			const value = fields[fieldKey];
			const encodedValues = Array.isArray(value)
				? value.map((value) => encodeURIComponent(String(value)))
				: [encodeURIComponent(fields[fieldKey])];

			uriParameters.push(
				...encodedValues.map(encodedValue => `${encodedKey}=${encodedValue}`)
			);
		});

		return uriParameters.join('&');
	}

	static fetchText(
		url: string,
		protocol: string = 'GET',
		isAsync: boolean = true,
		ignoreConnectionStatus: boolean = false,
		dataInUrl: {} | null = null,
		dataInBody: BodyInit | undefined = undefined,
		isPlainTextPayload: boolean = true,
		withCredentials: boolean = true,
		preventRedirect: boolean = true,
		preventSecurityHeader: boolean = false,
		onProgress?: (e: ProgressEvent) => void,
		requestId?: string,
	): | Promise<{} & string> {
		const headers = ApiUtils.getSecurityHeaderIfNeeded(preventSecurityHeader);

		if (!isPlainTextPayload) {
			headers.push({
				key: HttpHeaderType.CONTENT_TYPE,
				value: 'application/x-www-form-urlencoded',
			});
		}

		return Utils.promiseXHR(protocol, url, headers, isAsync, dataInUrl, dataInBody, undefined, ignoreConnectionStatus, withCredentials, preventRedirect, onProgress, requestId)
			.then(response => {
				return [
					0,
					ServerStatusCode.OK,
					ServerStatusCode.NO_CONTENT,
				].includes(response.status)
					? Promise.resolve(response.responseText)
					: Promise.reject(response);
			})
			.catch(error => {
				return Promise.reject(error);
			});
	}

	static isResourceAvailable(url: string): Promise<XMLHttpRequest> {
		return new Promise<XMLHttpRequest>((resolve, reject) => {
			const xhr = new XMLHttpRequest();
			xhr.timeout = 1000;
			xhr.open('GET', url, true);

			xhr.onload = () => {
				if (xhr.status === 200) {
					resolve(xhr);
				} else {
					reject(xhr);
				}
			};

			xhr.ontimeout = () => {
				if (xhr.status === 0) {
					resolve(xhr);
				} else {
					reject(xhr);
				}
			};

			xhr.send();
		});
	}

	static parseDate(dateString: string): Date {
		const dateStringSplit = dateString.split(' ');
		const dateSplit = dateStringSplit[0].split('-').map(e => parseInt(e, 10));
		const timeSplit = dateStringSplit[1].split(':').map(e => parseInt(e, 10));
		return new Date(dateSplit[0], dateSplit[1] - 1, dateSplit[2], timeSplit[0], timeSplit[1], timeSplit[2]);
	}

	static stringIsInStringEnum(value: string, actionKeys: {}) {
		for (let k in actionKeys) {
			if (actionKeys.hasOwnProperty(k) && actionKeys[k] === value) {
				return true;
			}
		}

		return false;
	}

	static getUrlWithCorrectProtocol(url: string) {
		const isMobileApp = UserAgentUtils.isMobileApp;
		const isHttpsNeeded = isMobileApp || window.location.protocol === 'https:';
		const newProtocol = isHttpsNeeded ? 'https' : 'http';

		const urlProtocolRegex = /^(.+):\/\//;
		const regexMatch = urlProtocolRegex.exec(url);

		if (regexMatch === null) {
			return `${newProtocol}://${url}`;
		}

		return url.replace(regexMatch[1], newProtocol );
	}

	static getUrlSeparator(url: string): string {
		return url.includes('?') ? '&' : '?';
	}

	static getUrlWithHttpsProtocol(url: string): string {
		return url.replace('http:', 'https:');
	}

	static isSameUrl(url1: string, url2: string): boolean {
		const getUrlWithEndSlash = (url: string): string => (
			url[url.length - 1] === '/' ? url : url + '/'
		);

		return Utils.getUrlWithHttpsProtocol(getUrlWithEndSlash(url1)) === Utils.getUrlWithHttpsProtocol(getUrlWithEndSlash(url2));
	}

	static getUrlForSecuredResource(url: string): string {
		const { appInfos } = store.getState();
		if (!Utils.isMultiApp(appInfos) || !ApiUtils.token) {
			return url;
		}

		const urlWithToken = url + `${Utils.getUrlSeparator(url)}token=${ApiUtils.token}`;
		return Utils.getUrlWithHttpsProtocol(urlWithToken);
	}

	static getFileExtension(fileName: string): string {
		if (fileName) {
			const extensionRegex = /(?:\.([^.]+))?(?:\?.*)?$/;
			const regexMatch = extensionRegex.exec(fileName);
			return !!regexMatch && !!regexMatch[1]
				? regexMatch[1].split('?')[0] // remove query params
				: '';
		}
		return '';
	}

	static getScreenFormat(): ScreenFormat {
		if (UserAgentUtils.isMobileApp && UserAgentUtils.isPhone()) {
			return ScreenFormat.Mobile;
		} else if (UserAgentUtils.isMobileApp && UserAgentUtils.isTablet()) {
			return ScreenFormat.Tablet;
		} else {
			const screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

			if (screenWidth < 768) {
				return ScreenFormat.Mobile;
			} else if (screenWidth < 1200) {
				return ScreenFormat.Tablet;
			}
			return ScreenFormat.Desktop;
		}
	}

	public static generateId = (length: number = 15): string => {
		let text = '';
		let possibleChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

		for (let i = 0; i < length; i++) {
			text += possibleChars.charAt(Math.floor(Math.random() * possibleChars.length));
		}
		return text;
	}

	static getCookie(cookieName: string): string {
		const cookieList = document.cookie.split(';');
		const key = `${cookieName}=`;
		const selectedCookie = cookieList.find(element => element.includes(cookieName));

		return selectedCookie ? selectedCookie.substring(key.length) : '';
	}

	static setCookie(cookieName: string, value: string, expireDays: number): void {
		const expireDate = new Date();
		expireDate.setTime(expireDate.getTime() + (expireDays * 24 * 60 * 60 * 1000));
		document.cookie = `${cookieName}=${value};expires=${expireDate.toUTCString()}`;
	}

	static isSecure = (appType: AppType, needCredentials: boolean): boolean => {
		return appType === AppType.Multiple || needCredentials;
	}

	static isMultiApp = (appInfos: AppInfosState): boolean => {
		return appInfos.type === AppType.Multiple;
	}

	static isMobileMultiApp = (appInfos: AppInfosState): boolean => {
		return UserAgentUtils.isMobileApp && Utils.isMultiApp(appInfos);
	}

	static isValidEmail = (email: string): boolean => {
		const emailRegex = /(.+)@(.+){2,}\.(.+){2,}/;
		return emailRegex.test(email);
	}

	static isValidUrl = (url: string): boolean => {
		const urlRegex = /^http(s)?:\/\/(www\.)?.+[.].+$/;
		return urlRegex.test(url);
	}

	static isMenuItemTypeRoute = (itemType: MenuItemType): boolean => {
		const nonRouteTypes = [MenuItemType.Asset, MenuItemType.Link, MenuItemType.Folder, MenuItemType.Page, MenuItemType.SearchButton];
		return !nonRouteTypes.includes(itemType);
	}

	static uncapitalize = (captilizedString: string): string => {
		return `${captilizedString.charAt(0).toLowerCase()}${captilizedString.substring(1)}`;
	}

	static getCurrentFolderId = (galleryId: number, routerParamsHistory: RouterParamsState[] ): number | undefined => {
		const currentRoute = routerParamsHistory[routerParamsHistory.length - 1];
		const { params } = currentRoute;
		const { origin, originParam } = params;
		if (origin === DriveItemListOrigin.Folder) {
			return originParam ? parseInt(originParam, 10) : galleryId;
		}
		return;
	}

	static getGalleryMeta = (driveList: DriveListState, galleryConfig: Api.Config): DriveItemInfo => {
		const { driveItems } = driveList;
		const galleryId = galleryConfig.applicationGallery;
		return driveItems[galleryId];
	}

	static getPrivateDirId = (driveList: DriveListState, galleryConfig: Api.Config): number | undefined => {
		const galleryMeta = Utils.getGalleryMeta(driveList, galleryConfig);
		return galleryMeta ? galleryMeta.privateDirId : undefined;
	}

	static getSharedDirId = (driveList: DriveListState, galleryInfos: GalleryInfosState): number | undefined => {
		const galleryMeta = Utils.getGalleryMeta(driveList, galleryInfos.config);
		return galleryMeta ? galleryMeta.sharedDirId : undefined;
	}

	static getFolderIdForPrivateResource = (): number | undefined => {
		const {
			galleryInfos: { config },
			routerParamsHistory,
			driveList,
		} = store.getState();

		const currentFolderId = Utils.getCurrentFolderId(config.applicationGallery, routerParamsHistory);
		const privateDirId = Utils.getPrivateDirId(driveList, config);
		const isPrivateDirDescendant = DriveItemUtils.isPrivateDirDescendant(currentFolderId, privateDirId, driveList.driveItems);

		return currentFolderId && isPrivateDirDescendant
			? currentFolderId
			: privateDirId;
	}

	static getViewportHeight = () => Math.max(
		document.documentElement.clientHeight,
		window.innerHeight || 0
	)

	// This static property uses getViewportHeight to get its value and must be declared after
	// tslint:disable-next-line
	static initialViewportHeight = Utils.getViewportHeight();

	static getKeyboardHeight = () => Utils.getViewportHeight() - Utils.initialViewportHeight;

	static itemsToById = (items: {}[], idKey: string = 'id') => (
		items.reduce((byId, item) => {
			byId[item[idKey]] = item;
			return byId;
		}, {})
	)

	static cancelRequest(requestId: string) {
		const request = Utils.cancelableRequests[requestId];
		if (request) {
			request.abort();
		}
	}

	static getCssClasses(classes: string, conditionalClasses: { [k: string]: boolean }): string {
		return Object.keys(conditionalClasses).reduce((acc, current) => {
			acc += conditionalClasses[current] ? ` ${current}` : '';
			return acc;
		}, classes);
	}

	static isEmptyObject(obj: Object): boolean {
		for (let prop in obj) {
			if (obj.hasOwnProperty(prop)) {
				return false;
			}
		}

		return true;
	}

	private static handleNativeApiBug(
		xhr: XMLHttpRequest,
		resolve: (value?: XMLHttpRequest | PromiseLike<XMLHttpRequest> | undefined) => void,
		reject: (reason?: {}) => void,
		protocol: string,
		url: string,
		headers: HttpHeader[],
		isAsync: boolean,
		dataInUrl: {} | null,
		dataInBody: BodyInit | undefined,
		attempt: number,
		ignoreConnectionStatus: boolean,
		withCredentials: boolean,
		preventRedirect: boolean,
		onProgress?: (e: ProgressEvent) => void,
		requestId?: string,
	) {
		if (attempt < 3) {
			Utils.promiseXHR(protocol, url, headers, isAsync, dataInUrl, dataInBody, attempt + 1, ignoreConnectionStatus, withCredentials, preventRedirect, onProgress, requestId)
				.then(res => resolve(res))
				.catch(res => reject(res));
		} else {
			reject(xhr);
		}
	}
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/index.ts